import {
  AirTimeRegistrationImpl,
  IAirTimeRegistrationRepository,
} from "api/merchantRepository/airTimeRegistration/AirTimeRegistrationRepository";
import {
  CashFlowImpl,
  ICashFlowRepository,
} from "api/merchantRepository/cashFlow/CashFlowRepository";
import {
  FilterFeatureImpl,
  IFilterFeatureRepository,
} from "api/merchantRepository/filterFeature/FilterFeatureRepository";
import {
  IMobiSimRegistrationRepository,
  MobiSimRegistrationImpl,
} from "api/merchantRepository/mobiSimRegistration/MobiSimRegistrationRepository";
import {
  INiceSimRegistrationRepository,
  NiceSimRegistrationImpl,
} from "api/merchantRepository/niceSimRegistration/NiceSimRegistrationRepository";
import {
  INotificationRepository,
  NotificationImpl,
} from "api/merchantRepository/notification/NotificationRepository";
import {
  IPackageRegistrationRepository,
  PackageRegistrationImpl,
} from "api/merchantRepository/packageRegistration/PackageRegistrationRepository";
import {
  ISupportFeatureRepository,
  SupportFeatureImpl,
} from "api/merchantRepository/supportFeature/SupportFeatureRepository";
import {
  ITravelSimRegistrationRepository,
  TravelSimRegistrationImpl,
} from "api/merchantRepository/travelSimRegistration/TravelSimRegistrationRepository";
import {
  AppTransactionImpl,
  IAppTransactionRepository,
} from "api/repositories/appTransaction/AppTransactionRepository";
import {
  AuthenticationsImpl,
  IAuthenticationsRepository,
} from "api/repositories/authentications/AuthenticationsRepository";
import {
  CarrierImpl,
  ICarrierRepository,
} from "api/repositories/carrier/CarrierRepository";
import {
  GeneralImpl,
  IGeneralRepository,
} from "api/repositories/general/GeneralRepository";
import {
  IUserManagementRepository,
  UserManagementImpl,
} from "api/repositories/userManagement/UserManagementRepository";
import { NotificationViewModel } from "components/header/notification/viewModel/NotificationViewModel";
import { AirTimeRegistrationViewModel } from "features/airTimeRegistration/viewModel/AirTimeRegistrationViewModel";
import { CashFlowViewModel } from "features/cashFlow/viewModel/CashFlowViewModel";
import { LoginViewModel } from "features/login/viewModel/LoginViewModel";
import { MobiSimRegistrationViewModel } from "features/mobiSimRegistration/viewModel/MobiSimRegistrationViewModel";
import { NiceSimRegistrationViewModel } from "features/niceSimRegistration/viewModel/NiceSimRegistrationViewModel";
import { PackageRegistrationViewModel } from "features/packageRegistration/viewModel/PackageRegistrationViewModel";
import { ApiDocumentViewModel } from "features/supportFeature/viewModel/ApiDocumentViewModel";
import { SupportFeatureViewModel } from "features/supportFeature/viewModel/SupportFeatureViewModel";
import { OutboundRegistrationViewModel } from "features/travelSimRegistration/viewModel/OutboundRegistrationViewModel";
import { TravelSimRegistrationViewModel } from "features/travelSimRegistration/viewModel/TravelSimRegistrationViewModel";
import { container } from "tsyringe";
import { constructor } from "tsyringe/dist/typings/types";
import { InjectionToken } from "utils/injectionToken/InjectionToken";

export class DependenciesInjection {
  static alreadyInjected: boolean = false;
  static registeredToken: Array<string | symbol> = [];
  static resolvedToken: Map<string | symbol, any> = new Map();

  private static register<T>(
    token: string | symbol,
    implClass: constructor<T>,
    type: "viewModel" | "otherRepo"
  ) {
    if (!container.isRegistered(token)) {
      container.register<T>(token, { useClass: implClass });
      if (type === "otherRepo") {
        this.registeredToken.push(token);
      }
    }
  }

  private static injectAuthentication() {
    this.register<IAuthenticationsRepository>(
      InjectionToken.IAuthenticationsRepository,
      AuthenticationsImpl,
      "otherRepo"
    );
    this.register<LoginViewModel>(
      InjectionToken.LoginViewModel,
      LoginViewModel,
      "viewModel"
    );
  }

  private static injectDashboard() {
    this.register<IGeneralRepository>(
      InjectionToken.IGeneralRepository,
      GeneralImpl,
      "otherRepo"
    );
  }
  private static injectUserManagement() {
    this.register<IUserManagementRepository>(
      InjectionToken.IUserManagementRepository,
      UserManagementImpl,
      "otherRepo"
    );
  }

  private static injectPackageRegistration() {
    this.register<IAppTransactionRepository>(
      InjectionToken.IAppTransactionRepository,
      AppTransactionImpl,
      "otherRepo"
    );
    this.register<IPackageRegistrationRepository>(
      InjectionToken.IPackageRegistrationRepository,
      PackageRegistrationImpl,
      "otherRepo"
    );
    this.register<PackageRegistrationViewModel>(
      InjectionToken.PackageRegistrationViewModel,
      PackageRegistrationViewModel,
      "viewModel"
    );
  }

  private static injectNiceSimRegistration() {
    this.register<INiceSimRegistrationRepository>(
      InjectionToken.INiceSimRegistrationRepository,
      NiceSimRegistrationImpl,
      "otherRepo"
    );
    this.register<NiceSimRegistrationViewModel>(
      InjectionToken.NiceSimRegistrationViewModel,
      NiceSimRegistrationViewModel,
      "viewModel"
    );
  }

  private static injectMobiSimRegistration() {
    this.register<IMobiSimRegistrationRepository>(
      InjectionToken.IMobiSimRegistrationRepository,
      MobiSimRegistrationImpl,
      "otherRepo"
    );
    this.register<MobiSimRegistrationViewModel>(
      InjectionToken.MobiSimRegistrationViewModel,
      MobiSimRegistrationViewModel,
      "viewModel"
    );
  }

  private static injectTravelSimRegistration() {
    this.register<ITravelSimRegistrationRepository>(
      InjectionToken.ITravelSimRegistrationRepository,
      TravelSimRegistrationImpl,
      "otherRepo"
    );
    this.register<TravelSimRegistrationViewModel>(
      InjectionToken.TravelSimRegistrationViewModel,
      TravelSimRegistrationViewModel,
      "viewModel"
    );
    this.register<OutboundRegistrationViewModel>(
      InjectionToken.OutboundRegistrationViewModel,
      OutboundRegistrationViewModel,
      "viewModel"
    );
  }

  private static injectAirTimeRegistration() {
    this.register<IAirTimeRegistrationRepository>(
      InjectionToken.IAirTimeRegistrationRepository,
      AirTimeRegistrationImpl,
      "otherRepo"
    );
    this.register<AirTimeRegistrationViewModel>(
      InjectionToken.AirTimeRegistrationViewModel,
      AirTimeRegistrationViewModel,
      "viewModel"
    );
  }

  private static injectCarrier() {
    this.register<ICarrierRepository>(
      InjectionToken.ICarrierRepository,
      CarrierImpl,
      "otherRepo"
    );
  }

  private static injectSupportFeature() {
    this.register<ISupportFeatureRepository>(
      InjectionToken.ISupportFeatureRepository,
      SupportFeatureImpl,
      "otherRepo"
    );
    this.register<INotificationRepository>(
      InjectionToken.INotificationRepository,
      NotificationImpl,
      "otherRepo"
    );
    this.register<SupportFeatureViewModel>(
      InjectionToken.SupportFeatureViewModel,
      SupportFeatureViewModel,
      "viewModel"
    );
    this.register<ApiDocumentViewModel>(
      InjectionToken.ApiDocumentViewModel,
      ApiDocumentViewModel,
      "viewModel"
    );
  }

  private static injectNoti() {
    this.register<INotificationRepository>(
      InjectionToken.INotificationRepository,
      NotificationImpl,
      "otherRepo"
    );
    this.register<NotificationViewModel>(
      InjectionToken.NotificationViewModel,
      NotificationViewModel,
      "viewModel"
    );
  }

  private static injectFilterFeature() {
    this.register<IFilterFeatureRepository>(
      InjectionToken.IFilterFeatureRepository,
      FilterFeatureImpl,
      "otherRepo"
    );
  }

  private static injectCashFlow() {
    this.register<ICashFlowRepository>(
      InjectionToken.ICashFlowRepository,
      CashFlowImpl,
      "otherRepo"
    );
    this.register<CashFlowViewModel>(
      InjectionToken.CashFlowViewModel,
      CashFlowViewModel,
      "viewModel"
    );
  }

  private static registerAllTokens() {
    this.injectAuthentication();
    this.injectDashboard();
    this.injectUserManagement();
    this.injectAirTimeRegistration();
    this.injectPackageRegistration();
    this.injectNiceSimRegistration();
    this.injectMobiSimRegistration();
    this.injectTravelSimRegistration();
    this.injectSupportFeature();
    this.injectCarrier();
    this.injectFilterFeature();
    this.injectCashFlow();
    this.injectNoti();
    //Register DI trong method này, lưu ý cần khai báo token trong class InjectionToken
  }

  private static checkInjectionAndResolve() {
    this.registeredToken.forEach((token) => {
      if (!container.isRegistered(token)) {
        throw new Error(`Unregistered token: ${String(token.toString())}`);
      }

      const dependency = container.resolve(token);
      this.resolvedToken.set(token, dependency);
    });
  }

  static lateResolution<T>(token: string | symbol): T {
    if (!token) {
      throw new Error("lateResolution: token is undefined");
    }
    if (!container.isRegistered(token)) {
      throw new Error(`Unregistered token: ${String(token.toString())}`);
    }
    const singleDependency = container.resolve(token);
    return singleDependency as unknown as T;
  }

  static async initInjections() {
    if (this.alreadyInjected) return;
    this.registerAllTokens();
    this.checkInjectionAndResolve();
  }

  static getRepo<T>(token: string | symbol): T {
    const dependency = this.resolvedToken.get(token);
    if (!dependency) {
      throw new Error(`Token ${String(token.toString())} not found`);
    }
    return dependency;
  }
}
